'use client';

import { useEffect, useRef, useState } from 'react';
import { INK } from './tokens';

type Props = {
  citation: string;
  copyLabel: string;
  copiedLabel: string;
};

/** The citation line with a one-tap copy affordance. */
export function CiteBlock({ citation, copyLabel, copiedLabel }: Props) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(citation);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions, older browsers): the text is
      // selectable, so the user can still copy manually.
    }
  };

  return (
    <div>
      <blockquote
        className="text-sm leading-relaxed select-all border-l-2 pl-4"
        style={{ borderColor: INK.accent, color: INK.soft }}
      >
        {citation}
      </blockquote>
      <button
        type="button"
        onClick={copy}
        className="mt-4 inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest appearance-none border-0 bg-transparent p-0 cursor-pointer hover:opacity-70 transition-opacity"
        style={{ color: copied ? INK.accent : INK.base }}
        aria-live="polite"
      >
        {copied ? copiedLabel : copyLabel}
        <span aria-hidden style={{ color: INK.accent }}>
          {copied ? '✓' : '⧉'}
        </span>
      </button>
    </div>
  );
}
